import { z } from 'zod/v4';
import {
  solarCanonicalJson,
  solarCommittedFace,
  type SolarCommittedFace,
} from './solarCommittedInputs';
import { solarRoofPlaneSchema } from './solarMounting';
import {
  frozenSolarWeatherSnapshot,
  type SolarWeatherGeneration,
} from './solarWeatherGeneration';

export const solarGenerationStatusSchema = z.enum([
  'valid',
  'needs_recalculation',
  'unavailable',
  'legacy_unverified',
]);
export type SolarGenerationStatus = z.infer<typeof solarGenerationStatusSchema>;
export const solarGenerationDimensionsSchema = z.object({
  widthMeters: z.number().positive(),
  heightMeters: z.number().positive(),
  capacityWatts: z.number().positive(),
});
export const solarMonthlyGenerationSchema = z.object({
  version: z.literal(1),
  source: z.enum(['google_monthly_flux', 'legacy', 'unavailable']),
  status: z.enum(['valid', 'legacy_unverified', 'unavailable']),
  inputFingerprint: z.string().max(1_000_000).nullable(),
  sourceInputFingerprint: z.string().max(1_000_000).nullable().optional(),
  effectivePlane: solarRoofPlaneSchema,
  panelDimensions: solarGenerationDimensionsSchema,
  historicalPanels: z
    .array(
      z.object({
        identity: z.string(),
        monthlyShares: z.array(z.number().nonnegative()).length(12),
      })
    )
    .max(5_000),
});
export type SolarMonthlyGeneration = z.infer<
  typeof solarMonthlyGenerationSchema
>;
export const solarGenerationProvenanceSchema = z.object({
  version: z.literal(1),
  status: solarGenerationStatusSchema,
  source: z.enum(['legacy', 'google_flux', 'yourpower', 'mcs', 'unavailable']),
  calculatedAt: z.string().datetime().nullable(),
  /** Canonical, exact input identity, deliberately not a lossy hash. */
  inputFingerprint: z.string().max(1_000_000).nullable(),
  effectivePlane: solarRoofPlaneSchema,
  panelDimensions: solarGenerationDimensionsSchema,
  lossModel: z.enum([
    'legacy_unknown',
    'google_dc_ac_0_85',
    'provider',
    'mcs_standard',
  ]),
  historicalPanels: z
    .array(z.object({ identity: z.string(), annualAcKwh: z.number() }))
    .optional(),
  /** Monthly shape has independent satellite inputs; MCS annual yield cannot certify it. */
  monthly: solarMonthlyGenerationSchema.optional(),
});
export type SolarGenerationProvenance = z.infer<
  typeof solarGenerationProvenanceSchema
>;
export type SolarGenerationDimensions = z.infer<
  typeof solarGenerationDimensionsSchema
>;
export const parseSolarGeneration = (
  value: unknown
): SolarGenerationProvenance | undefined => {
  const parsed = solarGenerationProvenanceSchema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
};
type Point = { lat: number; lng: number };
export type SolarGenerationFace = SolarCommittedFace & {
  pitchDegrees: number;
  azimuthDegrees: number;
  polygon: { outer: Point[]; holes: Point[][] };
  generation?: SolarGenerationProvenance;
};
type Panel = {
  corners: Point[];
  orientation: string;
  mcsYearlyEnergyKwh?: number | null;
};

export const solarGenerationFingerprint = (
  face: SolarGenerationFace,
  panels: readonly Panel[],
  dimensions: SolarGenerationDimensions,
  lossModel: SolarGenerationProvenance['lossModel']
): string =>
  solarCanonicalJson({
    version: 2,
    committed: solarCommittedFace(face),
    plane: [face.pitchDegrees, ((face.azimuthDegrees % 360) + 360) % 360],
    roof: {
      outer: face.polygon.outer.map((p) => [p.lat, p.lng]),
      holes: face.polygon.holes.map((ring) => ring.map((p) => [p.lat, p.lng])),
    },
    panels: panels
      .map((panel) =>
        JSON.stringify([
          panel.orientation,
          panel.corners.map((p) => [p.lat, p.lng]),
        ])
      )
      .sort(),
    module: [
      dimensions.widthMeters,
      dimensions.heightMeters,
      dimensions.capacityWatts,
    ],
    lossModel,
  });

/** Adopting old output records provenance, never certifies it as a fresh result. */
export const legacySolarGeneration = (
  face: Omit<SolarGenerationFace, 'generation'>,
  panels: readonly Panel[],
  dimensions: SolarGenerationDimensions
): SolarGenerationProvenance => ({
  version: 1,
  source: 'legacy',
  status: 'legacy_unverified',
  calculatedAt: null,
  inputFingerprint: solarGenerationFingerprint(
    face,
    panels,
    dimensions,
    'legacy_unknown'
  ),
  effectivePlane: {
    pitchDegrees: face.pitchDegrees,
    azimuthDegrees: face.azimuthDegrees,
  },
  panelDimensions: dimensions,
  lossModel: 'legacy_unknown',
  historicalPanels: panels.flatMap((panel) =>
    'annualAcKwh' in panel && typeof panel.annualAcKwh === 'number'
      ? [
          {
            identity: JSON.stringify([panel.orientation, panel.corners]),
            annualAcKwh: panel.annualAcKwh,
          },
        ]
      : []
  ),
});
export const unavailableSolarGeneration = (
  face: SolarGenerationFace,
  dimensions: SolarGenerationDimensions
): SolarGenerationProvenance => ({
  version: 1,
  source: 'unavailable',
  status: 'unavailable',
  calculatedAt: null,
  inputFingerprint: null,
  effectivePlane: {
    pitchDegrees: face.pitchDegrees,
    azimuthDegrees: face.azimuthDegrees,
  },
  panelDimensions: dimensions,
  lossModel: 'legacy_unknown',
});

/** An analysis records no provenance for its own output; unreadable provenance is unavailable. */
export const initialiseSolarGeneration = (
  raw: unknown,
  face: Omit<SolarGenerationFace, 'generation'>,
  panels: readonly Panel[],
  dimensions: SolarGenerationDimensions
): SolarGenerationProvenance =>
  raw == null
    ? legacySolarGeneration(face, panels, dimensions)
    : (parseSolarGeneration(raw) ??
      unavailableSolarGeneration(face, dimensions));

export const recordedMonthlyGeneration = (
  face: SolarGenerationFace,
  panels: readonly (Panel & { monthlyShares?: number[] | null })[],
  dimensions: SolarGenerationDimensions,
  edited = false
): SolarMonthlyGeneration => ({
  version: 1,
  source: edited ? 'unavailable' : 'legacy',
  status: edited ? 'unavailable' : 'legacy_unverified',
  inputFingerprint: edited
    ? null
    : solarGenerationFingerprint(face, panels, dimensions, 'legacy_unknown'),
  sourceInputFingerprint: edited
    ? null
    : solarGenerationFingerprint(face, panels, dimensions, 'legacy_unknown'),
  effectivePlane: {
    pitchDegrees: face.pitchDegrees,
    azimuthDegrees: face.azimuthDegrees,
  },
  panelDimensions: dimensions,
  historicalPanels: panels.flatMap((panel) =>
    panel.monthlyShares?.length === 12
      ? [
          {
            identity: JSON.stringify([panel.orientation, panel.corners]),
            monthlyShares: panel.monthlyShares,
          },
        ]
      : []
  ),
});

/** Compare satellite inputs, never the independently recalculated annual fingerprint. */
export const projectMonthlyGeneration = (
  monthly: SolarMonthlyGeneration,
  face: SolarGenerationFace,
  panels: readonly Panel[],
  dimensions: SolarGenerationDimensions,
  includedSubset = false
): SolarMonthlyGeneration => {
  const fingerprint = solarGenerationFingerprint(
    face,
    panels,
    dimensions,
    'legacy_unknown'
  );
  let matches = monthly.inputFingerprint === fingerprint;
  if (!matches && includedSubset && monthly.inputFingerprint) {
    try {
      const original = JSON.parse(monthly.inputFingerprint);
      const current = JSON.parse(fingerprint);
      matches =
        JSON.stringify({ ...original, panels: current.panels }) === fingerprint;
    } catch {
      matches = false;
    }
  }
  const identities = new Set(
    monthly.historicalPanels.map((panel) => panel.identity)
  );
  const available =
    face.appliedArrayAzimuthDegrees !== null &&
    monthly.source !== 'unavailable' &&
    matches &&
    panels.length > 0 &&
    panels.every((panel) =>
      identities.has(JSON.stringify([panel.orientation, panel.corners]))
    );
  const matchedStatus =
    monthly.source === 'legacy' ? 'legacy_unverified' : 'valid';
  return {
    ...monthly,
    status: available ? matchedStatus : 'unavailable',
    inputFingerprint:
      available && includedSubset ? fingerprint : monthly.inputFingerprint,
  };
};

export const faceGenerationStatus = (
  face: SolarGenerationFace,
  panels: readonly Panel[],
  dimensions: SolarGenerationDimensions
): SolarGenerationStatus => {
  const result = face.generation;
  if (face.appliedArrayAzimuthDegrees === null) return 'unavailable';
  if (!result) return 'legacy_unverified';
  if (result.source === 'unavailable' || !result.inputFingerprint)
    return 'unavailable';
  if (
    result.source === 'mcs' &&
    panels.some((panel) => panel.mcsYearlyEnergyKwh == null)
  )
    return 'unavailable';
  if (
    result.inputFingerprint !==
    solarGenerationFingerprint(face, panels, dimensions, result.lossModel)
  )
    return 'needs_recalculation';
  return result.source === 'legacy' ? 'legacy_unverified' : 'valid';
};
export const generationStatusHasResult = (
  status: SolarGenerationStatus
): boolean => status === 'valid' || status === 'legacy_unverified';

/** One included invalid roof makes the system total unavailable, never a subtotal. */
export const solarGenerationAvailability = (args: {
  faces: readonly (SolarGenerationFace & { id: number })[];
  panels: readonly (Panel & { faceId: number; included: boolean })[];
  excludedFaceIds?: readonly number[];
  dimensions: SolarGenerationDimensions;
}): SolarGenerationStatus => {
  if (
    args.panels.some(
      (panel) =>
        panel.included && !args.faces.some((face) => face.id === panel.faceId)
    )
  )
    return 'unavailable';
  const statuses = args.faces
    .filter(
      (face) =>
        !args.excludedFaceIds?.includes(face.id) &&
        args.panels.some((panel) => panel.faceId === face.id && panel.included)
    )
    .map((face) =>
      faceGenerationStatus(
        face,
        args.panels.filter((panel) => panel.faceId === face.id),
        args.dimensions
      )
    );
  if (statuses.includes('needs_recalculation')) return 'needs_recalculation';
  if (!statuses.length || statuses.includes('unavailable'))
    return 'unavailable';
  return statuses.includes('legacy_unverified') ? 'legacy_unverified' : 'valid';
};

export const GENERATION_RECALCULATION_MESSAGE =
  'Generation needs recalculation after mounting changes';

export const persistedSolarGenerationAvailability = (
  geometry: {
    faces: readonly SolarGenerationFace[];
    packedPanels: readonly (Panel & { faceIndex: number })[];
    panelDimensions: SolarGenerationDimensions;
    weatherGeneration?: {
      result: SolarWeatherGeneration;
      geometryFingerprint: string;
    };
  },
  includedIndexes?: readonly number[] | null
): SolarGenerationStatus => {
  if (geometry.weatherGeneration) {
    if (frozenSolarWeatherSnapshot(geometry, includedIndexes)) return 'valid';
    return geometry.weatherGeneration.result.status === 'unavailable'
      ? 'unavailable'
      : 'needs_recalculation';
  }
  const included = includedIndexes?.length ? new Set(includedIndexes) : null;
  return solarGenerationAvailability({
    faces: geometry.faces.map((face, id) => ({ ...face, id })),
    panels: geometry.packedPanels.map((panel, index) => ({
      ...panel,
      faceId: panel.faceIndex,
      included: !included || included.has(index),
    })),
    dimensions: geometry.panelDimensions,
  });
};

/** Frozen document read compatibility only; this does not certify new forecasts or writes. */
export const frozenDocumentMonthlyGenerationStatus = (
  geometry: {
    faces: readonly SolarGenerationFace[];
    packedPanels: readonly (Panel & {
      faceIndex: number;
      monthlyShares?: number[] | null;
    })[];
    panelDimensions: SolarGenerationDimensions;
    edited?: boolean;
  },
  includedIndexes?: readonly number[] | null
): 'valid' | 'legacy_unverified' | 'unavailable' => {
  const included = includedIndexes?.length ? new Set(includedIndexes) : null;
  const panels = geometry.packedPanels.filter(
    (_, index) => !included || included.has(index)
  );
  if (
    !panels.length ||
    panels.some(
      (panel) =>
        panel.monthlyShares?.length !== 12 || !geometry.faces[panel.faceIndex]
    )
  )
    return 'unavailable';
  const statuses = [...new Set(panels.map((panel) => panel.faceIndex))].map(
    (index) => {
      const face = geometry.faces[index];
      const monthly = face.generation?.monthly;
      // Pre-contract snapshots already contain their historical monthly chart.
      // The edited flag cannot retroactively remove it or certify its source.
      if (!monthly) return 'legacy_unverified' as const;
      return projectMonthlyGeneration(
        monthly,
        face,
        panels.filter((panel) => panel.faceIndex === index),
        geometry.panelDimensions,
        true
      ).status;
    }
  );
  if (statuses.includes('unavailable')) return 'unavailable';
  return statuses.includes('legacy_unverified') ? 'legacy_unverified' : 'valid';
};
